import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4601" />} data-id="element-4600">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4602">
        <motion.div variants={itemVariants} data-id="element-4603">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4605" />} data-id="element-4604" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4606">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4608" />} data-id="element-4607" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4609">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4611" />} data-id="element-4610" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4612">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4614" />} data-id="element-4613" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4615">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4616">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4617">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4618">
            <div className="flex items-center justify-between" data-id="element-4619">
              <div className="flex items-center gap-2" data-id="element-4620">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4621" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4622">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4623">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4624">
              <div className="flex items-center gap-2" data-id="element-4625">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4626" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4627">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4628">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4629" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4630" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4631">
            <div className="flex items-center justify-between" data-id="element-4632">
              <div className="flex items-center gap-3" data-id="element-4633">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4634" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4635" />}
                <div data-id="element-4636">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4637">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4638">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4639">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4640" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4641">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4642">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4643" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4644" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4645">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4646">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4647">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4648">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4649">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4650">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4651">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4652">
                    <div className="text-lg font-bold text-red-700" data-id="element-4653">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4654">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4655">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4656" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4657" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4658" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4659">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4660">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4661">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4662">
            <Clock className="w-4 h-4" data-id="element-4663" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4664">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4665">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4666">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4667" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4668" /> : <Activity className="w-4 h-4" data-id="element-4669" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4670">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4671">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4672">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4673">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4674">
            <Server className="w-4 h-4" data-id="element-4675" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4676">
            <div className="flex items-center justify-between" data-id="element-4677">
              <span className="text-sm text-gray-600" data-id="element-4678">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4679">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4680">
              <span className="text-sm text-gray-600" data-id="element-4681">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4682">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4683">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4684">
                <span className="text-sm text-gray-600" data-id="element-4685">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4686">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4687">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4688" />
              </div>
            </div>
            <div data-id="element-4689">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4690">
                <span className="text-sm text-gray-600" data-id="element-4691">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4692">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4693">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4694" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4695">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4696">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4697">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4698">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4699">
                <div className="flex justify-between text-xs mb-1" data-id="element-4700">
                  <span className="text-gray-600" data-id="element-4701">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4702">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4703">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4704" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4705">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4706">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4707" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4708">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4709">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4710">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4711">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4712">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4713">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4714">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4715">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4716">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4717" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4718">
            <div className="flex items-end justify-between" data-id="element-4719">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4720">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4721">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4722">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4723" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4724">
              <div className="flex justify-between" data-id="element-4725">
                <span className="text-gray-500" data-id="element-4726">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4727">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4728">
                <span className="text-gray-500" data-id="element-4729">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4730">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}